import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Phishing-resistant by default',
    description:
      'FIDO2 and WebAuthn are first-class citizens. Push, biometric, and hardware-key flows that can not be replayed or socially engineered.',
    bullets: [
      'FIDO2 / WebAuthn with platform authenticators',
      'Hardware keys (YubiKey, Titan, Feitian)',
      'Push approval through Monofor Identity',
      'TOTP / HOTP as fallback only',
    ],
  },
  {
    title: 'Adaptive, not annoying',
    description:
      'Risk-aware policies prompt for MFA when something looks off, not on every coffee-shop login. Users get out of the way; attackers do not.',
    bullets: [
      'Risk scoring on device, network, and behavior',
      'Step-up MFA on sensitive actions',
      'Execute Flow: gate logins behind approval workflows',
      'Trusted-device remembering with TTL',
      'Geographic and impossible-travel rules',
    ],
  },
  {
    title: 'Works everywhere users actually work',
    description:
      'MFA on the web is the easy part. Monosign covers the workstation, the VPN, the RDP session, and the privileged terminal too.',
    bullets: [
      'MFA on Windows / macOS / Linux logon',
      'Passwordless macOS sign-in via QR or push',
      'MFA on RDP, SSH, and VPN gateways',
      'MFA on RADIUS-backed infrastructure',
      'Offline OTP for disconnected scenarios',
    ],
  },
  {
    title: 'Painless enrollment, recoverable',
    description:
      'Self-service enrollment from any device, with admin-controlled fallback paths for lost devices and break-glass accounts.',
    bullets: [
      'QR-driven enrollment in under a minute',
      'Self-service reset with identity proofing',
      'Help-desk-assisted recovery flow',
      'Break-glass accounts with separate policy',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Pick your factors',
    description:
      'Decide which factors you allow (FIDO2, push, biometric, TOTP) and which you require for high-risk apps.',
  },
  {
    title: 'Enroll your users',
    description:
      'Users self-enroll from a phone or hardware key. Admin policies enforce minimum-factor strength.',
  },
  {
    title: 'Enforce, log, prove',
    description:
      'Every challenge is logged, scored, and exportable for SOC 2 and ISO 27001 evidence.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Single Sign-On',
    description:
      'Federate every app behind one identity. SAML 2 / OIDC / OAuth 2.',
    href: '/sign/sso',
  },
  {
    label: 'Monofor Identity',
    description:
      'Mobile authenticator for push, OTP, and biometric approval.',
    href: '/sign/authenticator',
  },
  {
    label: 'ITDR',
    description:
      'Detections that trigger step-up MFA automatically.',
    href: '/sign/itdr',
  },
]

export default function MfaPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Multi-Factor Authentication (MFA)"
        description="Phishing-resistant MFA with FIDO2, WebAuthn, push, and hardware keys. Adaptive policies. MFA for web, desktop, VPN, RDP, and SSH."
        path="/sign/mfa"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · Multi-Factor Authentication"
          title={
            <>
              MFA that
              <br />
              attackers can not phish.
            </>
          }
          body="FIDO2, WebAuthn, push, biometrics, and hardware keys: applied adaptively, enrolled in a minute, enforced everywhere from web apps to the Windows logon screen."
          badges={[
            'FIDO2 / WebAuthn ready',
            'MFA on web, desktop, VPN, RDP',
            'Self-service enrollment',
          ]}
        />
        <SolutionPillars
          title={<>Strong factors. Smart prompts. Real evidence.</>}
          body="Monosign MFA blocks credential phishing without grinding users down. It picks the right factor for the right risk and gives your auditors something concrete to hold."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From zero to phishing-resistant.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
